import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import {
  CLIENTS_ADMIN_ACCESS,
  ROLE_BUSINESS_DEVELOPMENT_LEAD,
  ROLE_CLIENT_ADMIN,
  ROLE_CONTACT_ADMIN,
  ROLE_SUPER_ADMIN,
  ROUTE_CLIENTS_DETAILS,
  ROUTE_CLIENTS_EDIT,
  ROUTE_CLIENTS_RELATIONSHIP_REVIEW,
} from "../../SharedModule/utils/constants";
import {
  hasSuperAdminAccess,
  verifyModuleAccess,
} from "../../SharedModule/utils/accessUtils";
import {
  checkClientCreatedByCostCenter,
  checkCouldDeactivate,
} from "../utils/clientsUtils";
import { ModalPortal } from "../../SharedModule/components/ModalPortal";
import { ModalDeactivateClient } from "../components/ModalDeactivateClient";
import { useModal } from "../../SharedModule/hooks/useModal";
import { useToggleClientActivation } from "./useToggleClientActivation";
import {
  setIsClientDetailInfoLoading,
  setNeedClientInfoUpdate,
} from "../redux/reducers/clientDetails.reducer";
import {
  setIsActualClientForHeaderInfoLoading,
  setNeedHeaderRefresh,
  setNeedRefresh,
} from "../redux/reducers/clientsList.reducer";
import { Client } from "../types/clients-module-types";

// Shared by ClientBillingInfoPage and ClientDetailPage: edit-profile
// navigation, the deactivate/reactivate flow and its modal, and the
// edit/toggle-status button visibility rules are wired up identically in
// both. isExternalClient() itself stays page-specific (BillingInfoPage only
// checks HubSpot external data; DetailPage also checks clientDetails.isExternal)
// and is passed in rather than shared, so each page keeps its own definition.
export const useClientActivationActions = (
  id: string | undefined,
  ClientForHeaderInfo: Client,
  clientDetails: any,
  activeUserId: string,
  userRolesList: any,
  actualClientExternalData: any,
  isExternalClient: () => boolean,
  isLoadingWorkInfo: boolean,
  clientRecurringBillings: any,
  clientWorkOrders: any,
  clientProjects: any
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const goToEditProfile = () => {
    // if client is external
    if (isExternalClient()) {
      let url =
        process.env.REACT_APP_HUBSPOT_BASE_URI +
        "/contacts/" +
        process.env.REACT_APP_HUBSPOT_ACCOUNT_ID +
        "/company/" +
        actualClientExternalData.externalId;

      const newWindow = window.open(url, "_blank");
      if (newWindow) {
        newWindow.focus();
      }
    } else if (
      checkClientCreatedByCostCenter(ClientForHeaderInfo.companyName)
    ) {
      const costCentersUrl: any =
        process.env.REACT_APP_APPSMITH_COST_CENTERS_URL;
      const newWindow = window.open(costCentersUrl, "_blank");
      if (newWindow) {
        newWindow.focus();
      }
    } else {
      navigate(ROUTE_CLIENTS_EDIT.replace(":id", String(id)), {
        state: {
          from:
            location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
              ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
              : "",
        },
      });
    }
  };

  // Modal config
  const modalRef = useRef<undefined>(undefined);
  const showModalDeactivateClient = () => {
    (modalRef.current as any).show(true);
  };
  const closeModalDeactivateClient = () => {
    (modalRef.current as any).show(false);
  };

  const [isLoadingDeactivate, setIsLoadingDeactivate] = useState(false);

  const { showModal, closeModal, Modal, ...modalProps } = useModal();

  const deactivateClient = useToggleClientActivation(
    "deactivate",
    showModal,
    closeModal,
    isExternalClient(),
    setIsLoadingDeactivate
  );

  const reactivateClient = useToggleClientActivation(
    "reactivate",
    showModal,
    closeModal,
    isExternalClient(),
    setIsLoadingDeactivate
  );

  const deactivateClientAction = async () => {
    if (
      !checkCouldDeactivate(
        clientRecurringBillings,
        clientWorkOrders,
        clientProjects
      )
    ) {
      showModalDeactivateClient();
    } else {
      let response = await deactivateClient({ ClientId: Number(id) });
      if (response.success) {
        dispatch(setIsActualClientForHeaderInfoLoading(true));
        dispatch(setNeedHeaderRefresh(true));
        dispatch(setIsClientDetailInfoLoading(true));
        dispatch(setNeedClientInfoUpdate(true));
        dispatch(setNeedRefresh(true));
        navigate(ROUTE_CLIENTS_DETAILS.replace(":id", String(id)), {
          state: {
            from:
              location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                : "",
          },
        });
      }
    }
  };

  const reactivateClientAction = async () => {
    let response = await reactivateClient({ ClientId: Number(id) });
    if (response.success) {
      dispatch(setIsActualClientForHeaderInfoLoading(true));
      dispatch(setNeedHeaderRefresh(true));
      dispatch(setIsClientDetailInfoLoading(true));
      dispatch(setNeedClientInfoUpdate(true));
      dispatch(setNeedRefresh(true));
      navigate(ROUTE_CLIENTS_DETAILS.replace(":id", String(id)), {
        state: {
          from:
            location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
              ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
              : "",
        },
      });
    }
  };

  const checkCouldSeeToggleStatusButton = (isActive: boolean) => {
    if (clientDetails && ClientForHeaderInfo) {
      return (
        isActive &&
        // clients created by CC only deactivate in appsmith
        !checkClientCreatedByCostCenter(ClientForHeaderInfo.companyName) &&
        // super admin & client admin could activate/deactivate all
        (verifyModuleAccess(
          true,
          [ROLE_SUPER_ADMIN, ROLE_CLIENT_ADMIN],
          userRolesList
        ) ||
          // is external => contact admin and engagement lead in cc they own
          (clientDetails?.isExternal &&
            (verifyModuleAccess(true, [ROLE_CONTACT_ADMIN], userRolesList) ||
              (verifyModuleAccess(
                true,
                [ROLE_BUSINESS_DEVELOPMENT_LEAD],
                userRolesList
              ) &&
                clientDetails.companyOwnerUserId === activeUserId))))
      );
    } else {
      return false;
    }
  };

  const couldSeeEditButton = () => {
    if (isExternalClient()) {
      return (
        verifyModuleAccess(true, CLIENTS_ADMIN_ACCESS, userRolesList) ||
        (!verifyModuleAccess(true, CLIENTS_ADMIN_ACCESS, userRolesList) &&
          verifyModuleAccess(true, [ROLE_BUSINESS_DEVELOPMENT_LEAD], userRolesList) &&
          clientDetails.companyOwnerUserId === activeUserId)
      );
    }
    if (checkClientCreatedByCostCenter(ClientForHeaderInfo.companyName)) {
      return hasSuperAdminAccess(userRolesList);
    }
    return verifyModuleAccess(true, CLIENTS_ADMIN_ACCESS, userRolesList);
  };

  const getEditButtonLabel = (): string => {
    if (isExternalClient()) return "Edit Profile in HubSpot ";
    if (checkClientCreatedByCostCenter(ClientForHeaderInfo.companyName)) {
      return "Edit Cost Center ";
    }
    return "Edit Profile ";
  };

  const deactivateModalPortal = (
    <ModalPortal ref={modalRef}>
      {!isLoadingWorkInfo && (
        <ModalDeactivateClient
          clientRecurringBillings={clientRecurringBillings}
          clientWorkOrders={clientWorkOrders}
          clientProjects={clientProjects}
          btnCancel={closeModalDeactivateClient}
          type="formModal"
        />
      )}
    </ModalPortal>
  );

  return {
    Modal,
    modalProps,
    closeModal,
    deactivateModalPortal,
    isLoadingDeactivate,
    goToEditProfile,
    deactivateClientAction,
    reactivateClientAction,
    checkCouldSeeToggleStatusButton,
    couldSeeEditButton,
    getEditButtonLabel,
  };
};
